import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchAccountingReferencesDetails } from './fetchAccountingReferencesDetails';
import { mapAccountingReferencesDetailsRow } from './mapAccountingReferencesDetailsRow';
import {
  AccountingReferencesDetailsQueryParams,
  AccountingReferencesDetailsResponse,
} from './types';
import { zodAccountingReferencesDetailsQueryParamsSchema } from './validations';

/**
 * Generates the query options for the accounting references details query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the accounting references details.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getAccountingReferencesDetailsQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: Partial<AccountingReferencesDetailsQueryParams>;
  options?: Omit<
    UseQueryOptions<AccountingReferencesDetailsResponse>,
    'queryKey' | 'queryFn'
  >;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodAccountingReferencesDetailsQueryParamsSchema.parse(queryParams);

  const isEnabled = Boolean(
    validatedQueryParams.dateRange.dateFrom &&
    validatedQueryParams.dateRange.dateTo,
  );

  return {
    enabled: isEnabled,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchAccountingReferencesDetails(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey:
      queryKeysAnalytics.getAccountingReferencesDetails(validatedQueryParams),
    select: mapAccountingReferencesDetailsRow,
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the paginated accounting references details data with given query parameters.
 * Handles pagination and prefetches the next page for better performance.
 *
 * @param queryParams - The query parameters for fetching the accounting references details.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Accounting-Reference/operation/get_accounting_reference_activity_table_accounting_reference_statistics_details_post
 */
export const useQueryAccountingReferencesDetails = (
  queryParams: Partial<AccountingReferencesDetailsQueryParams>,
  options?: Omit<
    UseQueryOptions<
      AccountingReferencesDetailsResponse,
      Error,
      AccountingReferencesDetailsResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getAccountingReferencesDetailsQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
